/* ==========================================================================
   KIND OF BLUE — JS de tienda. Vanilla, sin frameworks, sin dependencias.
   Web Components encapsulados (patrón Horizon) + un par de utilidades.
   ========================================================================== */
(() => {
  'use strict';

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];

  /* ---------------------------------------------------- formato de precio CLP
     $34.990 — punto de miles, sin decimales.

     OJO, dos formateadores a propósito: Shopify no es consistente.
     - cart.js / cart/add.js y `product.variants | json` devuelven CENTAVOS
       (39990 CLP llega como 3999000)            -> clp()
     - /search/suggest.json devuelve la UNIDAD MAYOR como string ("39990")
       -> clpMayor(). Usar clp() acá mostraba "$400" en vez de "$39.990". */
  const clp = (cents) => '$' + Math.round(cents / 100).toLocaleString('es-CL');
  const clpMayor = (v) => '$' + Math.round(Number(v) || 0).toLocaleString('es-CL');

  const bus = {
    emit(name, detail) { document.dispatchEvent(new CustomEvent(name, { detail })); },
    on(name, fn) { document.addEventListener(name, fn); },
  };

  /* ======================================================================
     1 · BARRA DE ANUNCIOS — rota los mensajes que haya, sin urgencia
     ====================================================================== */
  class KobAnuncios extends HTMLElement {
    connectedCallback() {
      this.msgs = $$('.anun__msg', this);
      if (this.msgs.length < 2) {
        if (this.msgs[0]) this.msgs[0].setAttribute('data-on', '');
        return;
      }
      this.i = 0;
      this.msgs[0].setAttribute('data-on', '');
      const ms = parseInt(this.dataset.intervalo, 10) || 5200;
      if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.timer = setInterval(() => this.next(), ms);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    next() {
      this.msgs[this.i].removeAttribute('data-on');
      this.i = (this.i + 1) % this.msgs.length;
      this.msgs[this.i].setAttribute('data-on', '');
    }
  }
  customElements.define('kob-anuncios', KobAnuncios);

  /* ======================================================================
     2 · PANELES (carrito, filtros, buscador) — apertura/cierre + foco
     ====================================================================== */
  /* 4.6 · Cerrados, la bolsa, el buscador y el menú del teléfono quedan
     `inert`: con aria-hidden solo, sus enlaces y campos seguían recibiendo
     el foco con Tab estando fuera de la pantalla. El panel de filtros no:
     en escritorio es una columna visible. */
  const INERTES = ['.cartd', '.busca', '.menu'];
  INERTES.forEach((s) => { const el = $(s); if (el && !el.hasAttribute('data-on')) el.inert = true; });

  const paneles = {
    abrir(sel, foco) {
      const el = $(sel);
      if (!el) return;
      el.inert = false;
      el.setAttribute('data-on', '');
      el.removeAttribute('aria-hidden');
      document.documentElement.style.overflow = 'hidden';
      const back = $('.panel-back');
      if (back && sel !== '.busca') back.setAttribute('data-on', '');
      this.ultimoFoco = document.activeElement;
      const f = foco ? $(foco, el) : el.querySelector('button,input,a');
      if (f) setTimeout(() => f.focus(), 90);
    },
    cerrar(sel) {
      const el = $(sel);
      if (!el) return;
      el.removeAttribute('data-on');
      el.setAttribute('aria-hidden', 'true');
      if (INERTES.includes(sel)) el.inert = true;
      if (!$('[data-on].cartd, [data-on].side, [data-on].busca, [data-on].menu')) {
        document.documentElement.style.overflow = '';
        const back = $('.panel-back');
        if (back) back.removeAttribute('data-on');
      }
      if (this.ultimoFoco) this.ultimoFoco.focus();
    },
    cerrarTodo() {
      ['.cartd', '.side', '.busca', '.menu'].forEach((s) => {
        const el = $(s);
        if (el) { el.removeAttribute('data-on'); el.setAttribute('aria-hidden', 'true'); if (INERTES.includes(s)) el.inert = true; }
      });
      document.documentElement.style.overflow = '';
      const back = $('.panel-back');
      if (back) back.removeAttribute('data-on');
    },
  };

  document.addEventListener('click', (e) => {
    const ab = e.target.closest('[data-abrir]');
    if (ab) {
      e.preventDefault();
      /* desde el menú móvil, abrir otro panel cierra el menú primero */
      if (ab.closest('.menu') && ab.dataset.abrir !== '.menu') paneles.cerrar('.menu');
      paneles.abrir(ab.dataset.abrir, ab.dataset.foco);
      return;
    }
    const ce = e.target.closest('[data-cerrar]');
    if (ce) { e.preventDefault(); paneles.cerrar(ce.dataset.cerrar); return; }
    if (e.target.classList.contains('panel-back')) paneles.cerrarTodo();
    if (e.target.classList.contains('busca')) paneles.cerrar('.busca');
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') paneles.cerrarTodo(); });

  /* ======================================================================
     3 · CARRITO — drawer nativo de Shopify, restilizado
     ====================================================================== */
  const carro = {
    async añadir(id, cantidad = 1, boton) {
      const txt = boton ? boton.textContent : null;
      if (boton) { boton.disabled = true; boton.textContent = 'Agregando…'; }
      try {
        const r = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: cantidad }] }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err.description || 'No se pudo agregar');
        }
        await this.refrescar();
        paneles.abrir('.cartd');
        if (boton) boton.textContent = 'Agregado';
        setTimeout(() => { if (boton) { boton.textContent = txt; boton.disabled = false; } }, 1400);
      } catch (err) {
        if (boton) { boton.textContent = txt; boton.disabled = false; }
        const aviso = $('.cartd__error');
        if (aviso) { aviso.textContent = err.message; aviso.hidden = false; }
      }
    },

    async cambiar(clave, cantidad) {
      const r = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: clave, quantity: cantidad }),
      });
      if (r.ok) await this.refrescar();
    },

    async refrescar() {
      const r = await fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-drawer`);
      if (!r.ok) return;
      const html = await r.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nuevo = $('.cartd__inner', doc);
      const actual = $('.cartd__inner');
      if (nuevo && actual) actual.replaceWith(nuevo);
      const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((x) => x.json());
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
      bus.emit('cart:updated', cart);
    },
  };

  document.addEventListener('click', (e) => {
    const add = e.target.closest('[data-add-variant]');
    if (add) {
      e.preventDefault();
      carro.añadir(add.dataset.addVariant, 1, add);
      return;
    }
    const q = e.target.closest('[data-qty]');
    if (q) {
      e.preventDefault();
      carro.cambiar(q.dataset.linea, parseInt(q.dataset.qty, 10));
    }
  });

  /* form de PDP: interceptar para que caiga en el drawer */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[data-pdp-form]');
    if (!form) return;
    e.preventDefault();
    const id = form.querySelector('[name="id"]').value;
    const cant = form.querySelector('[name="quantity"]');
    carro.añadir(id, cant ? parseInt(cant.value, 10) || 1 : 1, form.querySelector('[type="submit"]'));
  });

  /* ======================================================================
     4 · AUDIO — adelantos de 30 s resueltos en runtime
     La fuente es el CDN de Apple Music (iTunes Search API, con CORS) y, si
     Apple no tiene el disco, Deezer vía JSONP. Streaming directo, jamás se
     descarga ni se re-sube: el crédito con la fuente es parte del player.
     ====================================================================== */
  let audioActual = null;
  let botonActual = null;
  const mini = {
    el: null,
    montar() {
      if (this.el) return this.el;
      const d = document.createElement('div');
      d.className = 'mini';
      d.innerHTML = `
        <span class="mini__disc" aria-hidden="true"></span>
        <span class="mini__info">
          <span class="mini__track"></span>
          <span class="mini__meta"></span>
        </span>
        <button type="button" class="mini__stop" aria-label="Detener"></button>
        <span class="mini__bar"><i></i></span>`;
      d.querySelector('.mini__stop').addEventListener('click', pararAudio);
      document.body.appendChild(d);
      this.el = d;
      return d;
    },
    mostrar(track, artista, fuente) {
      const d = this.montar();
      d.querySelector('.mini__track').textContent = track;
      d.querySelector('.mini__meta').textContent = `${artista} · 30 s · vía ${fuente}`;
      d.setAttribute('data-on', '');
    },
    progreso(pct) { if (this.el) this.el.querySelector('.mini__bar i').style.width = pct + '%'; },
    ocultar() { if (this.el) this.el.removeAttribute('data-on'); },
  };

  function pararAudio() {
    if (audioActual) { audioActual.pause(); audioActual = null; }
    if (botonActual) {
      botonActual.removeAttribute('data-playing');
      const b = $('.bar i', botonActual);
      if (b) b.style.width = '0';
      const t = $('.txt', botonActual);
      if (t) t.textContent = '0:30';
      const l = $('.lbl', botonActual);
      if (l) l.textContent = 'Escuchar';
      botonActual = null;
    }
    mini.ocultar();
    $$('.pdp--sonando').forEach((el) => el.classList.remove('pdp--sonando'));
    $$('.card--sonando').forEach((el) => el.classList.remove('card--sonando'));
  }

  /* --- resolución: iTunes primero (CORS abierto), Deezer por JSONP -------- */
  const normaliza = (x) => (x || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();

  function deezerJSONP(q) {
    return new Promise((res) => {
      const cb = 'kobDz' + Math.floor(performance.now());
      const tag = document.createElement('script');
      const fin = (v) => { delete window[cb]; tag.remove(); res(v); };
      window[cb] = (data) => fin(data?.data?.[0] || null);
      tag.onerror = () => fin(null);
      tag.src = `https://api.deezer.com/search?q=${encodeURIComponent(q)}&limit=5&output=jsonp&callback=${cb}`;
      document.head.appendChild(tag);
      setTimeout(() => window[cb] && fin(null), 6000);
    });
  }

  async function resolverPreview(artista, album, pistaCurada) {
    /* clave v2: invalida cachés viejos que guardaron bonus tracks */
    const cacheKey = 'kob-prev2:' + normaliza(artista + ' ' + album + ' ' + (pistaCurada || ''));
    try {
      const hit = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (hit) return hit;
    } catch { /* storage lleno o bloqueado: se resuelve igual */ }

    const art = normaliza(artista);
    const pista = normaliza(pistaCurada || '');
    /* el núcleo del álbum ignora colas de edición: deluxe, remaster, aniversario */
    const nucleo = normaliza(album).replace(/ (deluxe|remaster\w*|edition|edicion|anniversary|expanded|bonus).*$/, '');
    const esVarios = !art || /varios|various/.test(art);

    const delArtista = (t) => {
      if (esVarios) return true;
      const a = normaliza(t.artistName);
      return a.includes(art) || art.includes(a);
    };
    const delAlbum = (t) => {
      const c = normaliza(t.collectionName).replace(/ (deluxe|remaster\w*|edition|edicion|anniversary|expanded|bonus).*$/, '');
      return c.includes(nucleo.slice(0, 22)) || nucleo.includes(c.slice(0, 22));
    };
    const esaPista = (t) => {
      const n = normaliza(t.trackName);
      return n.includes(pista) || pista.includes(n);
    };
    /* castiga versiones que no son la del disco */
    const esVersionRara = (t) => /\b(live|en vivo|acoustic|acustic|demo|mix\)|remix|karaoke|instrumental|cover|tribute)\b/
      .test(normaliza(t.trackName) + ' ' + normaliza(t.collectionName));

    const buscar = async (term) => {
      const r = await fetch(
        `https://itunes.apple.com/search?term=${encodeURIComponent(term)}`
        + `&media=music&entity=song&country=CL&limit=25`);
      const j = await r.json();
      return (j.results || []).filter((t) => t.previewUrl);
    };

    let out = null;
    try {
      /* 1 — la canción curada del disco (kob.audio_track), verificada */
      if (pista) {
        const term = esVarios ? `${pistaCurada} ${album}` : `${artista} ${pistaCurada}`;
        const cands = (await buscar(term)).filter((t) => esaPista(t) && (delArtista(t) || delAlbum(t)));
        cands.sort((a, b) =>
          (delAlbum(b) - delAlbum(a))
          || (esVersionRara(a) - esVersionRara(b))
          || (a.trackName.length - b.trackName.length));
        if (cands[0]) out = { url: cands[0].previewUrl, track: cands[0].trackName, fuente: 'Apple Music' };
      }
      /* 2 — sin curaduría o sin match: primer track del disco correcto,
             exigiendo artista Y álbum a la vez */
      if (!out) {
        const cands = (await buscar(`${artista} ${album}`)).filter((t) => delArtista(t) && delAlbum(t));
        cands.sort((a, b) =>
          (esVersionRara(a) - esVersionRara(b))
          || ((a.discNumber || 1) - (b.discNumber || 1))
          || ((a.trackNumber || 99) - (b.trackNumber || 99)));
        if (cands[0]) out = { url: cands[0].previewUrl, track: cands[0].trackName, fuente: 'Apple Music' };
      }
    } catch { /* sin red o sin CORS: cae a Deezer */ }

    if (!out) {
      const t = await deezerJSONP(pista ? `${artista} ${pistaCurada}` : `${artista} ${album}`);
      if (t && t.preview) {
        const okArtista = esVarios || !t.artist
          || normaliza(t.artist.name).includes(art) || art.includes(normaliza(t.artist.name));
        if (okArtista) out = { url: t.preview, track: t.title, fuente: 'Deezer' };
      }
    }
    if (out) { try { localStorage.setItem(cacheKey, JSON.stringify(out)); } catch { /* sin espacio */ } }
    return out;
  }

  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.play');
    if (!btn) return;
    e.preventDefault();
    if (botonActual === btn) { pararAudio(); return; }
    pararAudio();

    let src = btn.dataset.src;
    let track = btn.dataset.track || '';
    let fuente = btn.dataset.fuente || 'Apple Music';
    const lbl = $('.lbl', btn);

    if (!src) {
      if (lbl) lbl.textContent = 'Buscando…';
      const r = await resolverPreview(
        btn.dataset.artista || '', btn.dataset.album || '', btn.dataset.track || '');
      if (!r) {
        if (lbl) lbl.textContent = 'No disponible';
        btn.setAttribute('data-off', '');
        return;
      }
      src = r.url; track = r.track; fuente = r.fuente;
      btn.dataset.src = src; btn.dataset.track = track; btn.dataset.fuente = fuente;
      const cred = $('.fuente', btn);
      if (cred) cred.textContent = 'vía ' + fuente;
    }

    const a = new Audio(src);
    a.preload = 'metadata';
    audioActual = a;
    botonActual = btn;
    btn.setAttribute('data-playing', '');
    if (lbl) lbl.textContent = 'Sonando';
    const suena = btn.closest('.card__meta, .pdp__info')?.querySelector('[data-suena]');
    if (suena && track) suena.textContent = 'Suena: ' + track;
    mini.mostrar(track || btn.dataset.album || 'Adelanto', btn.dataset.artista || '', fuente);
    const pdp = btn.closest('.pdp');
    if (pdp) pdp.classList.add('pdp--sonando');
    /* en la card, los carretes del cassette (o nada, si es vinilo) */
    const card = btn.closest('.card');
    if (card) card.classList.add('card--sonando');

    a.addEventListener('timeupdate', () => {
      const dur = a.duration && isFinite(a.duration) ? Math.min(a.duration, 30) : 30;
      const pct = Math.min(100, (a.currentTime / dur) * 100);
      const bar = $('.bar i', btn);
      if (bar) bar.style.width = pct + '%';
      mini.progreso(pct);
      const rest = Math.max(0, Math.ceil(dur - a.currentTime));
      const t = $('.txt', btn);
      if (t) t.textContent = '0:' + String(rest).padStart(2, '0');
      if (a.currentTime >= 30) pararAudio();
    });
    a.addEventListener('ended', pararAudio);
    a.play().catch(pararAudio);
  });

  /* ======================================================================
     5b · TE RECOMENDAMOS — cuando una búsqueda no deja nada que llevarse
     Pedido de Nelson (25-09-2026): cuando no aparece el artista o el disco
     (sin resultados, o sólo agotados), recomendar algo que le va a encantar
     a quien buscó. Se decide así, en orden:
       1. Quién es: Deezer identifica al artista de lo que se escribió
          (artista, disco, canción o "artista disco"). Si es el nombre de un
          disco, gana el artista del disco, salvo que un artista que se llame
          exactamente así tenga más seguidores ("kid a" es Radiohead, no la
          banda Kid A).
       2. Si de ese artista hay discos con stock (buscaron un disco que no
          tenemos, o lo escribieron mal), esos primero.
       3. Los artistas parecidos según Deezer (/artist/{id}/related) que
          tenemos con stock, en el orden de Deezer, dos discos cada uno.
       4. Si faltan, el mismo estilo: el género de iTunes llevado a los
          estilos de la tienda, los de más demanda primero.
       5. Si no se sabe quién es, discos que no fallan: canónicos y de alta
          demanda.
     Lo que hay con stock sale de /collections/vinilos-y-cds?view=indice
     (templates/collection.indice.liquid), que se pide sólo acá y se guarda
     media hora en sessionStorage. Lo que se escribe viaja a Deezer e
     iTunes, igual que el nombre del disco cuando se aprieta Escuchar.
     ====================================================================== */
  const recomendador = (() => {
    const raiz = window.Shopify?.routes?.root || '/';
    const norm = (x) => (x || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/&/g, ' and ').replace(/^the /, '').replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
    /* ¿"texto" contiene "q" como palabras enteras? */
    const palabras = (texto, q) => q.length > 1 && ` ${texto} `.includes(` ${q} `);
    /* el título sin "(Remastered)", "[Deluxe]" ni colas de edición */
    const limpio = (t) => norm(String(t || '').replace(/\s*[([].*$/, ''));

    /* Buscar un equipo que no vendemos no merece discos recomendados. */
    const EQUIPO = /\b(tornamesa|tocadisco|parlante|audifono|amplificador|aguja|capsula|cable|receptor|preamp|bocina|equipo|pro ?ject|ortofon|audio ?technica|technics|rega|denon|marantz|yamaha|jbl|klipsch|sennheiser|fluance|victrola)/;

    /* Géneros de iTunes (en español, tienda CL) → estilos de la tienda.
       El orden importa: "rock en español" es latino antes que rock. */
    const ESTILOS = [
      [/rock en espanol|latin|urbano|reggaeton|cumbia|salsa|bolero|tropical|folclor|folk|cantautor|trova|tango|bachata|ranchera|chilen|nueva cancion|raices|musicas del mundo|world|andina/, 'musica-chilena-y-latina'],
      [/banda.? sonora|soundtrack|cine|anime/, 'bandas-sonoras'],
      [/metal|punk|hardcore/, 'metal-y-punk'],
      [/jazz|blues|bossa/, 'jazz-y-blues'],
      [/soul|funk|r b|hip hop|rap|disco|reggae|motown|gospel/, 'soul-funk-y-groove'],
      [/electr|dance|house|techno|ambient|experimental/, 'electronica-y-experimental'],
      [/indie/, 'indie'],
      [/alternativ|rock|grunge|psicodel|progres/, 'rock-y-alternativo'],
      [/pop/, 'pop'],
    ];
    const DEMANDA = { 'canonico-premium': 3, 'alta-demanda': 2, 'de-culto-escaso': 1 };
    /* los de más demanda arriba, con un poco de azar para no repetir siempre lo mismo */
    const peso = (d) => (DEMANDA[d.d] || 0) + (d.top ? 0.5 : 0) + Math.random() * 0.6;

    function jsonp(ruta) {
      return new Promise((res) => {
        const cb = 'kobRec' + Math.random().toString(36).slice(2);
        const tag = document.createElement('script');
        const fin = (v) => { delete window[cb]; tag.remove(); res(v); };
        window[cb] = (data) => fin(data || null);
        tag.onerror = () => fin(null);
        tag.src = `https://api.deezer.com/${ruta}${ruta.includes('?') ? '&' : '?'}output=jsonp&callback=${cb}`;
        document.head.appendChild(tag);
        setTimeout(() => window[cb] && fin(null), 6000);
      });
    }

    let indice = null;
    async function cargarIndice() {
      if (indice) return indice;
      const clave = 'kob-indice-v1';
      try {
        const c = JSON.parse(sessionStorage.getItem(clave) || 'null');
        if (c && Date.now() - c.t < 30 * 60 * 1000) return (indice = c.d);
      } catch { /* storage bloqueado: se pide igual */ }
      const pagina = (n) => fetch(`${raiz}collections/vinilos-y-cds?view=indice&page=${n}`).then((r) => r.json());
      const p1 = await pagina(1);
      const discos = [...p1.discos];
      for (let n = 2; n <= Math.min(p1.paginas || 1, 4); n++) discos.push(...(await pagina(n)).discos);
      indice = discos;
      try { sessionStorage.setItem(clave, JSON.stringify({ t: Date.now(), d: discos })); } catch { /* sin espacio */ }
      return indice;
    }

    async function quienEs(q) {
      const nq = norm(q);
      if (nq.length < 2) return null;
      const e = encodeURIComponent(q);
      const [ar, al, tr] = await Promise.all([
        jsonp(`search/artist?q=${e}&limit=5`),
        jsonp(`search/album?q=${e}&limit=10`),
        jsonp(`search/track?q=${e}&limit=10`),
      ]);
      const artistas = ar?.data || [];
      const discos = (al?.data || []).filter((d) => d.artist);
      const pistas = (tr?.data || []).filter((t) => t.artist);
      const conocido = (a) => (a?.nb_fan || 0) >= 100000;
      const calza = (t) => t === nq || t.startsWith(nq + ' ');
      const comoArtista = (a) => ({ artista: a, por: 'nombre' });

      /* "radiohead ok computer", "grace de jeff buckley": artista y obra juntos */
      const combo = discos.find((d) => palabras(nq, norm(d.artist.name)) && palabras(nq, limpio(d.title)))
        || pistas.find((t) => palabras(nq, norm(t.artist.name)) && palabras(nq, limpio(t.title)));
      if (combo) return { artista: combo.artist, por: 'obra', obra: combo.title };

      const exacto = artistas.find((a) => norm(a.name) === nq);
      const nombre = artistas.find((a) => conocido(a)
        && (palabras(norm(a.name), nq) || (norm(a.name).length >= 4 && palabras(nq, norm(a.name)))));
      const disco = discos.find((d) => calza(norm(d.title)) && d.record_type !== 'single');
      if (disco) {
        /* "bob marley" es el artista aunque también haya un disco con ese nombre */
        if (palabras(norm(disco.artist.name), nq)) return comoArtista(disco.artist);
        const rival = exacto || nombre;
        if (rival && rival.id !== disco.artist.id) {
          const delDisco = await jsonp(`artist/${disco.artist.id}`);
          if ((rival.nb_fan || 0) > (delDisco?.nb_fan || 0)) return comoArtista(rival);
        }
        return { artista: disco.artist, por: 'obra', obra: disco.title };
      }
      if (exacto) return comoArtista(exacto);
      if (nombre) return comoArtista(nombre);
      const cancion = pistas.filter((t) => calza(norm(t.title))).sort((a, b) => (b.rank || 0) - (a.rank || 0))[0];
      if (cancion) return { artista: cancion.artist, por: 'obra', obra: cancion.title };
      if (conocido(artistas[0])) return comoArtista(artistas[0]);
      return null;
    }

    async function estiloDe(nombre) {
      try {
        const r = await fetch(`https://itunes.apple.com/search?term=${encodeURIComponent(nombre)}`
          + '&entity=musicArtist&limit=1&country=CL');
        const g = norm((await r.json()).results?.[0]?.primaryGenreName || '');
        return (ESTILOS.find(([re]) => re.test(g)) || [])[1] || null;
      } catch { return null; }
    }

    /* → { razon, discos: [{a, t, h, e, d, top, i, p}] } o null */
    async function recomendar(q, cuantos = 4) {
      if (EQUIPO.test(norm(q))) return null;
      const [ind, quien] = await Promise.all([cargarIndice(), quienEs(q).catch(() => null)]);
      const porArtista = new Map();
      for (const d of ind) {
        const k = norm(d.a);
        if (!porArtista.has(k)) porArtista.set(k, []);
        porArtista.get(k).push(d);
      }
      const elegidos = [];
      const ya = new Set();
      const sumar = (lista, max) => {
        const orden = lista.map((d) => [peso(d), d]).sort((x, y) => y[0] - x[0]).map(([, d]) => d);
        let n = 0;
        for (const d of orden) {
          if (elegidos.length >= cuantos || n >= max) break;
          if (ya.has(d.h)) continue;
          elegidos.push(d); ya.add(d.h); n++;
        }
      };

      let razon = '';
      if (quien) {
        const a = quien.artista;
        sumar(porArtista.get(norm(a.name)) || [], cuantos);
        if (elegidos.length < cuantos) {
          const rel = await jsonp(`artist/${a.id}/related?limit=50`);
          for (const r of rel?.data || []) {
            const lista = porArtista.get(norm(r.name));
            if (lista) sumar(lista, 2);
            if (elegidos.length >= cuantos) break;
          }
        }
        if (elegidos.length < cuantos) {
          const estilo = await estiloDe(a.name);
          if (estilo) sumar(ind.filter((d) => d.e === estilo), cuantos);
        }
        if (elegidos.length) {
          const obra = quien.obra ? String(quien.obra).replace(/\s*[([].*$/, '') : '';
          razon = obra && norm(obra) !== norm(a.name)
            ? `No tenemos «${obra}», pero si te gusta ${a.name}, esto te va a encantar.`
            : `Si te gusta ${a.name}, esto te va a encantar.`;
        }
      }
      if (!elegidos.length) {
        sumar(ind.filter((d) => DEMANDA[d.d] >= 2), cuantos);
        razon = 'Discos que no fallan, elegidos en el mesón.';
      }
      return elegidos.length ? { razon, discos: elegidos } : null;
    }

    return { recomendar };
  })();

  /* La página de búsqueda sin resultados: mismas tarjetas que todo el sitio,
     pedidas una por una a /products/<handle>?view=tarjeta. */
  class KobRecomienda extends HTMLElement {
    async connectedCallback() {
      const q = (this.dataset.q || '').trim();
      if (!q) return;
      const r = await recomendador.recomendar(q).catch(() => null);
      if (!r) return;
      const raiz = window.Shopify?.routes?.root || '/';
      const tarjetas = await Promise.all(r.discos.map((d) =>
        fetch(`${raiz}products/${encodeURIComponent(d.h)}?view=tarjeta`)
          .then((x) => (x.ok ? x.text() : '')).catch(() => '')));
      const grid = $('.recomienda__grid', this);
      grid.innerHTML = tarjetas.join('');
      if (!grid.children.length) return;
      $$('.rv', grid).forEach((el) => el.setAttribute('data-in', ''));
      $('.recomienda__razon', this).textContent = r.razon;
      this.hidden = false;
    }
  }
  customElements.define('kob-recomienda', KobRecomienda);

  /* ======================================================================
     5 · BUSCADOR — predictive search por artista, álbum o CANCIÓN
     El tracklist va dentro del body_html del producto, así que el buscador
     nativo lo indexa: "tren al sur" encuentra a Los Prisioneros.
     ====================================================================== */
  class KobBuscador extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.res = $('.busca__res', this);
      if (!this.input || !this.res) return;
      this.cache = new Map();
      this.recs = new Map();
      this.input.addEventListener('input', () => this.debounce());
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const q = this.input.value.trim();
          /* options[fields] es obligatorio: por defecto Shopify NO busca en el
             cuerpo del producto, y ahi vive el tracklist. Sin esto, buscar
             "tren al sur" devuelve cero. */
          if (q) location.href = `${window.Shopify?.routes?.root || '/'}search`
            + `?q=${encodeURIComponent(q)}&type=product&options[prefix]=last`
            + `&options[fields]=title,body,vendor,product_type,tag`;
        }
      });
    }
    debounce() {
      clearTimeout(this.t);
      this.t = setTimeout(() => this.buscar(), 230);
    }
    async buscar() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.res.innerHTML = ''; return; }
      if (this.cache.has(q)) { this.pintar(this.cache.get(q), q); return; }
      const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json`
        + `?q=${encodeURIComponent(q)}`
        + `&resources[type]=product&resources[limit]=10`
        + `&resources[options][unavailable_products]=last&resources[options][fields]=title,body,vendor,product_type,variants.title`;
      try {
        const data = await fetch(url).then((r) => r.json());
        const items = data?.resources?.results?.products || [];
        this.cache.set(q, items);
        this.pintar(items, q);
      } catch { /* sin resultados: se deja el estado anterior */ }
    }
    pintar(items, q) {
      if (!items.length) {
        this.res.innerHTML = `<div class="busca__empty">
          Nada con “${this.esc(q)}”. Prueba con el artista, el disco o una canción —
          o escríbenos a <a href="https://instagram.com/kindofale" target="_blank" rel="noopener">@kindofale</a> y lo salimos a buscar.
        </div>
        <div class="busca__recs" hidden></div>`;
        this.recomendar(q);
        return;
      }
      this.res.innerHTML = items.map((p) => {
        const img = p.featured_image?.url
          ? `<img src="${p.featured_image.url}" alt="" width="56" height="56" loading="lazy">`
          : '<span></span>';
        /* si el match no está en el título, es una canción del tracklist */
        const enTitulo = (p.title || '').toLowerCase().includes(q.toLowerCase())
          || (p.vendor || '').toLowerCase().includes(q.toLowerCase());
        const pista = enTitulo ? '' : `<em>Canción en este disco</em>`;
        const handle = (p.url || '').split('/products/')[1]?.split('?')[0] || '';
        return `<div class="busca__item">
          <a href="${p.url}" style="display:contents">${img}</a>
          <span>
            <a href="${p.url}"><b>${this.esc(p.title)}</b></a>
            <span>${this.esc(p.vendor || '')}</span>
            ${pista}
          </span>
          <span style="display:grid;gap:6px;justify-items:end">
            <span class="busca__price">${p.price ? clpMayor(p.price) : ''}</span>
            ${p.available !== false && handle
              ? `<button type="button" class="card__add" data-add-handle="${handle}">Agregar</button>` : ''}
          </span>
        </div>`;
      }).join('');
      /* Ningún disco con stock ("bad bunny" encuentra "Bad", agotado; "cerati",
         dos agotados y de rebote la tornamesa): también va "Te recomendamos". */
      if (items.every((p) => p.available === false || (p.tags || []).includes('linea:audio'))) {
        this.res.insertAdjacentHTML('beforeend', '<div class="busca__recs" hidden></div>');
        this.recomendar(q);
      }
    }
    /* "Te recomendamos" bajo el vacío (sección 5b). Espera a que la persona
       deje de escribir, y sólo pinta si lo que se ve sigue siendo esa búsqueda. */
    recomendar(q) {
      clearTimeout(this.tr);
      this.tr = setTimeout(async () => {
        if (this.input.value.trim() !== q) return;
        if (!this.recs.has(q)) this.recs.set(q, await recomendador.recomendar(q).catch(() => null));
        const r = this.recs.get(q);
        const caja = $('.busca__recs', this.res);
        if (!r || !caja || this.input.value.trim() !== q) return;
        caja.innerHTML = '<p class="busca__recs-t">Te recomendamos</p><p class="busca__recs-r"></p>'
          + r.discos.map((d) => this.fila(d)).join('');
        $('.busca__recs-r', caja).textContent = r.razon;
        caja.hidden = false;
      }, 650);
    }
    fila(d) {
      const url = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(d.h)}`;
      /* sin loading="lazy": son cuatro miniaturas que se ven apenas aparecen */
      const img = d.i ? `<img src="${this.esc(d.i)}" alt="" width="56" height="56">` : '<span></span>';
      return `<div class="busca__item">
        <a href="${url}" style="display:contents">${img}</a>
        <span>
          <a href="${url}"><b>${this.esc(d.t)}</b></a>
          <span>${this.esc(d.a)}</span>
        </span>
        <span style="display:grid;gap:6px;justify-items:end">
          <span class="busca__price">${this.esc(d.p)}</span>
          <button type="button" class="card__add" data-add-handle="${this.esc(d.h)}">Agregar</button>
        </span>
      </div>`;
    }
    esc(s) {
      return String(s).replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    }
  }
  customElements.define('kob-buscador', KobBuscador);

  /* ======================================================================
     6 · FILTROS DE COLECCIÓN — Search & Discovery, sin recargar la página
     ====================================================================== */
  class KobFiltros extends HTMLElement {
    connectedCallback() {
      /* Delegación: las zonas (facetas, chips, sorts) se reemplazan por AJAX,
         así que los listeners no pueden vivir en los elementos mismos. */
      this.addEventListener('click', (e) => {
        const s = e.target.closest('[data-sort]');
        if (s && this.contains(s)) {
          e.preventDefault();
          const url = new URL(location.href);
          url.searchParams.set('sort_by', s.dataset.sort);
          url.searchParams.delete('page');
          this.ir(url.pathname + url.search);
          return;
        }
        /* facetas y chips son <a> server-side: sin JS igual funcionan */
        const a = e.target.closest('a[data-faceta], a[data-quitar]');
        if (a && this.contains(a)) {
          e.preventDefault();
          this.ir(a.getAttribute('href'));
        }
      });
      addEventListener('popstate', () => this.ir(location.href, false));
    }
    async ir(url, push = true) {
      this.setAttribute('data-cargando', '');
      try {
        const html = await fetch(url).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        ['[data-zona="grid"]', '[data-zona="facetas"]', '[data-zona="chips"]', '[data-zona="conteo"]',
          '[data-zona="sorts"]', '[data-zona="titulo"]', '[data-zona="num"]', '[data-zona="desc"]']
          .forEach((sel) => {
            const nuevo = $(sel, doc);
            const actual = $(sel, document);
            if (nuevo && actual) actual.innerHTML = nuevo.innerHTML;
          });
        /* el botón "Ver N discos" del panel móvil también cambia */
        const nf = $('.side__foot .btn', doc);
        const af = $('.side__foot .btn', this);
        if (nf && af) af.textContent = nf.textContent;
        if (push) history.pushState({}, '', url);
        revelar();
      } finally {
        this.removeAttribute('data-cargando');
      }
    }
  }
  customElements.define('kob-filtros', KobFiltros);

  /* ======================================================================
     7 · PDP — variantes
     ====================================================================== */
  class KobVariantes extends HTMLElement {
    connectedCallback() {
      this.datos = JSON.parse($('script[type="application/json"]', this)?.textContent || '[]');
      $$('[data-variante]', this).forEach((b) => {
        b.addEventListener('click', () => this.elegir(b.dataset.variante));
      });
    }
    elegir(id) {
      const v = this.datos.find((x) => String(x.id) === String(id));
      if (!v) return;
      $$('[data-variante]', this).forEach((b) => {
        b.setAttribute('aria-checked', String(b.dataset.variante) === String(id));
      });
      const input = $('form[data-pdp-form] [name="id"]');
      if (input) input.value = v.id;
      const precio = $('[data-precio]');
      if (precio) precio.textContent = clp(v.price);
      const comparar = $('[data-comparar]');
      if (comparar) {
        if (v.compare_at_price && v.compare_at_price > v.price) {
          comparar.textContent = clp(v.compare_at_price);
          comparar.hidden = false;
        } else { comparar.hidden = true; }
      }
      const btn = $('form[data-pdp-form] [type="submit"]');
      if (btn) {
        btn.disabled = !v.available;
        btn.textContent = v.available ? 'Agregar' : 'Sin stock';
      }
      const stock = $('[data-stock]');
      if (stock) {
        /* En audio no se dice "queda 1": es catálogo que se repone, no una
           copia única. Liquid ya lo omite al pintar; acá hay que respetarlo
           igual al cambiar de variante, si no reaparece. */
        const equipo = document.body.classList.contains('pdp--equipo');
        const ultima = v.available && v.inventory_quantity === 1 && !equipo;
        stock.classList.toggle('pdp__stock--ultima', ultima);
        stock.textContent = !v.available
          ? 'Sin stock por ahora'
          : (ultima ? 'Queda 1 copia' : '');
      }
      if (v.featured_image) {
        const img = $('.pdp__shot img');
        if (img) img.src = v.featured_image.src || v.featured_image;
      }
      /* los chips de formato y estado siguen a la variante: el disco tiene
         tags para todo lo que puede ser, pero acá se compra uno solo */
      const fila = $('.pdp__tags');
      if (fila) {
        [['formato', fila.dataset.idxFormato], ['estado', fila.dataset.idxEstado]].forEach(([g, i]) => {
          const n = parseInt(i, 10);
          if (!Number.isInteger(n) || n < 0) return;
          const elegido = String((v.options || [])[n] || '').toLowerCase();
          if (!elegido) return;
          $$(`.pdp__tag[data-grupo="${g}"]`, fila).forEach((li) => {
            li.hidden = (li.dataset.valor || '').toLowerCase() !== elegido;
          });
        });
      }
      history.replaceState({}, '', `${location.pathname}?variant=${v.id}`);
    }
  }
  customElements.define('kob-variantes', KobVariantes);

  /* ======================================================================
     8 · VIDEO DIFERIDO — el iframe entra sólo al hacer click
     ====================================================================== */
  document.addEventListener('click', (e) => {
    const p = e.target.closest('.video__play');
    if (!p) return;
    e.preventDefault();
    const cont = p.closest('.video');
    const id = cont?.dataset.vimeo;
    if (!id) return;
    const f = document.createElement('iframe');
    f.src = `https://player.vimeo.com/video/${id}?autoplay=1&title=0&byline=0&portrait=0&dnt=1`;
    f.allow = 'autoplay; fullscreen; picture-in-picture';
    f.title = cont.dataset.titulo || 'Video de la tienda';
    f.setAttribute('loading', 'lazy');
    cont.innerHTML = '';
    cont.appendChild(f);
  });

  /* ======================================================================
     9 · REVELADO AL SCROLL
     ====================================================================== */
  let obs = null;
  function revelar() {
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      $$('.rv').forEach((el) => el.setAttribute('data-in', ''));
      return;
    }
    if (!obs) {
      obs = new IntersectionObserver((entradas) => {
        entradas.forEach((en) => {
          if (en.isIntersecting) { en.target.setAttribute('data-in', ''); obs.unobserve(en.target); }
        });
      }, { rootMargin: '0px 0px 18% 0px', threshold: 0.01 });
    }
    $$('.rv:not([data-in])').forEach((el) => obs.observe(el));

    /* Red final: si algo quedó sin revelar a los 900 ms (observer que no
       dispara, contenedor de scroll raro, iframe del editor de temas), se
       muestra. En móvil el primer viewport se revela de inmediato. */
    $$('.rv:not([data-in])').forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < innerHeight * 1.1) el.setAttribute('data-in', '');
    });
    clearTimeout(revelar._t);
    revelar._t = setTimeout(() => {
      $$('.rv:not([data-in])').forEach((el) => el.setAttribute('data-in', ''));
    }, 900);
  }


  /* ======================================================================
     10 · ATAJOS — / y ⌘K abren el buscador
     ====================================================================== */
  document.addEventListener('keydown', (e) => {
    const enCampo = /INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName || '')
      || document.activeElement?.isContentEditable;
    if ((e.key === '/' && !enCampo) || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
      e.preventDefault();
      paneles.abrir('.busca', 'input[type=search]');
    }
  });

  /* ======================================================================
     11 · FOCUS TRAP — Tab no se escapa de los paneles abiertos
     ====================================================================== */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const panel = $('.cartd[data-on], .side[data-on], .busca[data-on], .menu[data-on]');
    if (!panel) return;
    const focos = $$('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])', panel)
      .filter((el) => el.offsetParent !== null || panel.classList.contains('busca'));
    if (!focos.length) return;
    const primero = focos[0];
    const ultimo = focos[focos.length - 1];
    if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    else if (!panel.contains(document.activeElement)) { e.preventDefault(); primero.focus(); }
  });

  /* ======================================================================
     12 · AGREGAR DESDE EL BUSCADOR — suggest no trae variant id: se pide
     el .js del producto y se agrega la primera variante disponible
     ====================================================================== */
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-add-handle]');
    if (!b) return;
    e.preventDefault();
    const txt = b.textContent;
    b.textContent = '…';
    try {
      const p = await fetch(`${window.Shopify?.routes?.root || '/'}products/${b.dataset.addHandle}.js`)
        .then((r) => r.json());
      /* Con los cassettes apagados en el tema (<html data-cassettes="no">),
         el Agregar del buscador nunca elige la variante Cassette. */
      const sinCas = document.documentElement.dataset.cassettes === 'no';
      const visibles = (p.variants || []).filter((x) => !(sinCas && /cassette/i.test(x.title || '')));
      const v = visibles.find((x) => x.available) || visibles[0] || p.variants?.[0];
      if (v) { paneles.cerrar('.busca'); await carro.añadir(v.id, 1, null); }
    } finally { b.textContent = txt; }
  });

  /* Acá vivía el cargador del embed de Instagram. La sección se sacó: se
     actualizaba a mano y no había forma de que fuera diaria sin una app de
     pago o un token de Meta que caduca cada 60 días. Con la sección se fue
     también el script de terceros de 150 KB. */


  /* ======================================================================
     14 · GALERÍA DE LA FICHA — los thumbnails cambian la imagen principal
     (audio y merch traen 3–4 fotos oficiales). Sin dependencias.
     ====================================================================== */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('.pdp__thumbs button');
    if (!b) return;
    const t = b.querySelector('img');
    const main = $('[data-pdp-main]');
    if (!t || !main) return;
    main.removeAttribute('srcset');
    main.src = t.dataset.full;
    if (t.dataset.alt) main.alt = t.dataset.alt;
    $$('.pdp__thumbs button').forEach((x) => x.removeAttribute('aria-current'));
    b.setAttribute('aria-current', 'true');
  });

  /* ======================================================================
     9 · HERO ROTATIVO
     ====================================================================== */
  class KobHero extends HTMLElement {
    connectedCallback() {
      this.lams = $$('[data-lam]', this);
      if (this.lams.length < 2) return;
      this.puntos = $$('[data-punto]', this);
      this.i = 0;
      this.pausado = false;
      this.intervalo = parseInt(this.dataset.intervalo || '0', 10);

      /* Si el sistema pide menos movimiento, no rota sola: quedan los puntos.
         Nadie debería marearse mirando la portada de una disquería. */
      this.quieto = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.puntos.forEach((b, n) => b.addEventListener('click', () => { this.ir(n); this.detener(); }));

      const btn = $('[data-pausa]', this);
      if (btn) {
        btn.addEventListener('click', () => {
          this.pausado = !this.pausado;
          btn.setAttribute('aria-pressed', String(this.pausado));
          const e = $('[data-etq]', btn);
          if (e) e.textContent = this.pausado ? 'Reanudar' : 'Pausar';
          if (this.pausado) this.detener(); else this.arrancar();
        });
      }

      /* Se frena mientras el mouse está encima o algo adentro tiene el foco:
         cambiar la lámina debajo del cursor de alguien que está leyendo es
         de mala educación. */
      this.addEventListener('mouseenter', () => this.detener());
      this.addEventListener('mouseleave', () => this.arrancar());
      this.addEventListener('focusin', () => this.detener());
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.arrancar();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.detener(); else this.arrancar();
      });

      this.arrancar();
    }
    disconnectedCallback() { this.detener(); }

    ir(n) {
      const total = this.lams.length;
      this.i = ((n % total) + total) % total;
      this.lams.forEach((l, k) => { l.hidden = k !== this.i; });
      this.puntos.forEach((b, k) => b.setAttribute('aria-selected', String(k === this.i)));
      /* El revelado al scroll ya pasó cuando la lámina estaba oculta, así que
         la que entra tiene que darse por revelada a mano. */
      $$('.rv', this.lams[this.i]).forEach((el) => el.classList.add('visto'));
    }
    arrancar() {
      this.detener();
      if (this.pausado || this.quieto || !this.intervalo) return;
      this.t = setInterval(() => this.ir(this.i + 1), this.intervalo);
    }
    detener() { clearInterval(this.t); }
  }
  customElements.define('kob-hero', KobHero);

  /* ---------------------------------------------------------------- arranque */
  const iniciar = () => { revelar(); carro.refrescar().catch(() => {}); };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', iniciar);
  } else { iniciar(); }
})();

/* Acá vivía el arranque del loop de portada al pasar el mouse por una card.
   Se sacó junto con el video: lo que se mira al pasar el mouse vuelve a ser
   el vinilo saliendo de la funda, que es CSS y no pesa nada. */
